import { registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import localeFrCa from "@angular/common/locales/fr-CA";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withHashLocation } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { ENVIRONMENT } from "@kiraah-shared/environment";
import { provideMobileSkeleton } from "@kiraah-shared/mobile-skeleton";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideToastr } from "ngx-toastr";
import { from, Observable } from "rxjs";
import { environment } from "../environments/environment";
import { routes } from "./app.routes";

registerLocaleData(localeFr, "fr");
registerLocaleData(localeFrCa, "fr-ca");

/** Azure Blob static website only serves index.html on `/`; hash routing is required outside local dev. */
const useHashRouting = environment.Environment !== "LOCAL";

function createTranslateLoader(http: HttpClient): TranslateLoader {
  const loaders = [
    new TranslateHttpLoader(http, "./assets/i18n/shared/", ".json"),
    new TranslateHttpLoader(http, "./assets/i18n/", ".json"),
  ];
  return {
    getTranslation: (lang: string): Observable<Record<string, unknown>> => {
      const promises = loaders.map((loader) =>
        loader
          .getTranslation(lang)
          .toPromise()
          .catch(() => ({}))
      );
      return from(Promise.all(promises).then((results) => Object.assign({}, ...results)));
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideToastr({
      timeOut: 5000,
      positionClass: "toast-top-center",
      preventDuplicates: true,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes, ...(useHashRouting ? [withHashLocation()] : [])),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: "fr",
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    { provide: LOCALE_ID, useValue: "fr" },
    { provide: ENVIRONMENT, useValue: environment },
    provideMobileSkeleton({
      gatewayUrl: environment.Api.GatewayUrl,
    }),
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
};
